/**
 * Admin configuration hook — persists admin settings to localStorage.
 * Access is gated to the specific admin pubkey defined below.
 */

import { useLocalStorage } from '@/hooks/useLocalStorage';

/** The only pubkey that may access the admin panel. */
export const ADMIN_PUBKEY = '6f7db55a0d92aa52d2fb3f52c39c320b04bd2cacbf9fb41d745124e5985d8b25';

// ── Types ────────────────────────────────────────────────────────────────────

export type ListMode = 'open' | 'whitelist' | 'blacklist';

export interface NpubEntry {
  /** hex pubkey */
  pubkey: string;
  /** optional human label */
  label?: string;
  /** unix timestamp when the entry was added */
  addedAt: number;
  /** optional reason */
  reason?: string;
}

export interface ContentFilter {
  id: string;
  type: 'keyword' | 'hashtag' | 'pubkey' | 'domain';
  value: string;
  action: 'hide' | 'flag';
  enabled: boolean;
  createdAt: number;
  description?: string;
}

export interface AdminConfig {
  /** Upload access mode */
  uploadMode: ListMode;
  /** Upload whitelist — only these npubs may upload when mode = 'whitelist' */
  uploadWhitelist: NpubEntry[];
  /** Upload blacklist — these npubs are blocked from uploading when mode = 'blacklist' or 'open' */
  uploadBlacklist: NpubEntry[];

  /** View access mode — affects whether videos from an npub appear in feeds */
  viewMode: ListMode;
  /** View whitelist — only videos from these npubs are shown when mode = 'whitelist' */
  viewWhitelist: NpubEntry[];
  /** View blacklist — videos from these npubs are hidden when mode = 'blacklist' or 'open' */
  viewBlacklist: NpubEntry[];

  /** Universal content filters */
  contentFilters: ContentFilter[];

  /** Site-wide maintenance message (empty = no message) */
  maintenanceMessage: string;

  /** Whether to enable strict content moderation */
  strictModeration: boolean;

  /**
   * Custom relay URLs used exclusively for search queries (NIP-50).
   * Leave empty to use the app's default relay pool.
   * Dedicated search relays (e.g. nostr.wine, relay.nostr.band) typically
   * index far more events and support richer full-text search.
   */
  searchRelays: string[];
}

export interface UploadRecord {
  pubkey: string;
  eventId: string;
  title: string;
  timestamp: number;
  size?: number;
  tags: string[];
}

export interface AdminAnalytics {
  /** Page view events [ timestamp ] */
  pageViews: number[];
  /** Upload records */
  uploads: UploadRecord[];
  /** Unique visitors (pubkeys) that have been seen */
  uniqueVisitors: string[];
  /** Search queries */
  searchQueries: { query: string; timestamp: number }[];
}

const DEFAULT_CONFIG: AdminConfig = {
  uploadMode: 'open',
  uploadWhitelist: [],
  uploadBlacklist: [],
  viewMode: 'open',
  viewWhitelist: [],
  viewBlacklist: [],
  contentFilters: [],
  maintenanceMessage: '',
  strictModeration: false,
  searchRelays: [],
};

const DEFAULT_ANALYTICS: AdminAnalytics = {
  pageViews: [],
  uploads: [],
  uniqueVisitors: [],
  searchQueries: [],
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAdminConfig() {
  const [config, setConfig] = useLocalStorage<AdminConfig>(
    'goblinbox:admin-config',
    DEFAULT_CONFIG,
  );

  const updateConfig = (updater: (c: AdminConfig) => AdminConfig) => {
    setConfig(updater);
  };

  return { config, updateConfig };
}

export function useAdminAnalytics() {
  const [analytics, setAnalytics] = useLocalStorage<AdminAnalytics>(
    'goblinbox:admin-analytics',
    DEFAULT_ANALYTICS,
  );

  const recordPageView = () => {
    setAnalytics(prev => ({
      ...prev,
      pageViews: [...(prev.pageViews ?? []), Math.floor(Date.now() / 1000)].slice(-10000),
    }));
  };

  const recordVisitor = (pubkey: string) => {
    setAnalytics(prev => {
      const visitors = prev.uniqueVisitors ?? [];
      if (visitors.includes(pubkey)) return prev;
      return { ...prev, uniqueVisitors: [...visitors, pubkey] };
    });
  };

  const recordUpload = (record: UploadRecord) => {
    setAnalytics(prev => ({
      ...prev,
      uploads: [...(prev.uploads ?? []), record].slice(-5000),
    }));
  };

  const recordSearch = (query: string) => {
    setAnalytics(prev => ({
      ...prev,
      searchQueries: [
        ...(prev.searchQueries ?? []),
        { query, timestamp: Math.floor(Date.now() / 1000) },
      ].slice(-1000),
    }));
  };

  return { analytics, recordPageView, recordVisitor, recordUpload, recordSearch };
}

/**
 * Returns the configured custom search relay URLs.
 * Components can use this to direct search queries to dedicated relays.
 */
export function useSearchRelays(): string[] {
  const { config } = useAdminConfig();
  return config.searchRelays ?? [];
}

// ── Access check helpers ──────────────────────────────────────────────────────

/**
 * Returns true if the given pubkey is allowed to upload based on the current admin config.
 */
export function canUpload(pubkey: string, config: AdminConfig): boolean {
  if (config.uploadMode === 'whitelist') {
    return config.uploadWhitelist.some(e => e.pubkey === pubkey);
  }
  if (config.uploadMode === 'blacklist' || config.uploadMode === 'open') {
    return !config.uploadBlacklist.some(e => e.pubkey === pubkey);
  }
  return true;
}

/**
 * Returns true if videos from the given pubkey should be visible.
 */
export function canView(pubkey: string, config: AdminConfig): boolean {
  if (config.viewMode === 'whitelist') {
    return config.viewWhitelist.some(e => e.pubkey === pubkey);
  }
  if (config.viewMode === 'blacklist' || config.viewMode === 'open') {
    return !config.viewBlacklist.some(e => e.pubkey === pubkey);
  }
  return true;
}
